import { useState, ChangeEvent, MouseEvent, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, InputLabel, ButtonBase } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import TouchRipple from '@material-ui/core/ButtonBase/TouchRipple'
import chevronDownIcon from '@iconify-icons/akar-icons/chevron-down'
import { Icon } from '@iconify/react'
import { DEFAULT_NAMESPACE } from 'config/i18n'
import { Option } from 'shared/types'
import { PaginatedAutocompleteProps } from './PaginatedAutocomplete.types'
import {
  Wrapper,
  ShowAllText,
  RippleWrapper,
  CustomPlaceholder,
  TextField,
  Loader,
  LOADER_SIZE,
} from './PaginatedAutocomplete.style'

const PaginatedAutocomplete = <ObjectType extends Option<string, number>>({
  options,
  isLoading,
  autocompleteProps: {
    selectedItems,
    handleSelectItem,
    textFieldValue,
    setTextFieldValue,
    handleClearSelection,
    setAutocompletePageNumber,
    autocompletePageNumber,
    areAllOptionsFetched,
    isOpen,
    setIsOpen,
  },
  optionalProps,
  id,
}: PaginatedAutocompleteProps<ObjectType>) => {
  const { t } = useTranslation(DEFAULT_NAMESPACE)
  const [isFocus, setIsFocus] = useState(false)
  const listboxRef = useRef<any>(null)
  const rippleRef = useRef<any | null>()
  const onRippleStart = (
    e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>
  ) => {
    if (rippleRef.current) return rippleRef.current.start(e)
  }
  const onRippleStop = (
    e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>
  ) => {
    if (rippleRef.current) rippleRef.current.stop(e)
  }
  const handleShowAllClick = () => {
    if (handleClearSelection) handleClearSelection()
    setIsOpen(false)
  }
  const onClose = () => {
    setTextFieldValue('')
    setIsOpen(false)
  }
  useEffect(() => {
    if (listboxRef && listboxRef.current) {
      listboxRef.current.scrollTop = 0
    }
  }, [isOpen])

  const onScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget
    const isAtTheBottomOfList =
      Math.abs(
        ~~(listboxNode.scrollTop + listboxNode.clientHeight) -
          listboxNode.scrollHeight
      ) <= 1
    if (isAtTheBottomOfList && !areAllOptionsFetched) {
      setAutocompletePageNumber(autocompletePageNumber + 1)
    }
  }

  const handleTextfieldChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => setTextFieldValue(e.target.value)

  return (
    <>
      {optionalProps?.label && <InputLabel>{optionalProps.label}</InputLabel>}
      <Wrapper isOpen={isOpen}>
        <Autocomplete
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={onClose}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
          options={options}
          disableCloseOnSelect
          inputValue={textFieldValue}
          disableClearable
          disablePortal
          filterOptions={options => options.map(option => option)}
          getOptionLabel={option => option.title as string}
          ListboxProps={{ onScroll, ref: listboxRef }}
          renderTags={undefined}
          onHighlightChange={undefined}
          popupIcon={
            isLoading ? (
              <Loader isOpen={isOpen} size={LOADER_SIZE} />
            ) : (
              <Icon icon={chevronDownIcon} />
            )
          }
          id={id}
          renderOption={option => (
            <>
              {option.value > 0 &&
              optionalProps?.multiselect &&
              selectedItems &&
              handleSelectItem ? (
                <ButtonBase
                  onClick={() =>
                    handleSelectItem(
                      option,
                      selectedItems.some(item => item.value === option.value)
                    )
                  }
                >
                  <Checkbox
                    checked={selectedItems.some(
                      item => item.value === option.value
                    )}
                    disableRipple
                    color="primary"
                  />
                  {option.title as string}
                </ButtonBase>
              ) : (
                <ButtonBase onClick={handleShowAllClick}>
                  <ShowAllText>{t(option.title as string)}</ShowAllText>
                </ButtonBase>
              )}
            </>
          )}
          renderInput={params => (
            <RippleWrapper onMouseDown={onRippleStart} onMouseUp={onRippleStop}>
              <TextField
                {...params}
                value={textFieldValue}
                placeholder={
                  !textFieldValue && !selectedItems?.length
                    ? optionalProps?.defaultPlaceholder
                    : ''
                }
                onChange={handleTextfieldChange}
                InputProps={{
                  ...params.InputProps,
                  startAdornment:
                    !textFieldValue && selectedItems?.length ? (
                      <CustomPlaceholder isFocused={isFocus}>
                        <p>{selectedItems[0].title}</p>
                        {selectedItems.length > 1 && (
                          <p>{`(+${selectedItems?.length - 1})`}</p>
                        )}
                      </CustomPlaceholder>
                    ) : undefined,
                }}
              />
              <TouchRipple ref={rippleRef} center={false} />
            </RippleWrapper>
          )}
        />
      </Wrapper>
    </>
  )
}
export default PaginatedAutocomplete
